import moment from 'moment'
import React from 'react'
import { ErrorGroupObservation, ErrorGroupObservationAffectedOrigin, ObservationState } from '../domain/Error'
import { extractServerDisplayNameFromHostname } from '../domain/Observation'
import MomentDate from '../momentDate'
import { Box, BoxBody, BoxHeader, BoxTitle } from '../ui/box'
import { ObservationIcon } from './ObservationIcon'
import { BottleneckView } from '../trace/bottleneck-view'
import ProfileTable from '../monitor/profile_table'
import Link from '../helper/link'
import LargeNumber from '../helper/large-number'
import store from '../app/store'
import Router from '../router'
import Navigator from '../app/navigator'

interface Props {
    errorGroup: ErrorGroupObservation
    router: Router
    parameters: { [key: string]: string | number }
    navigator: Navigator
    bottleneckBeta: boolean
}

export class ObservationDetails extends React.PureComponent<Props> {
    public updateIgnoreState = (route: string, type: string) => {
        const href = this.props.router.path(route, { type })
        fetch(href, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(response => {
            store.dispatch({ type: 'Errors.purge' })

            this.props.navigator.replace({
                ...this.props.parameters,
                reload: (+this.props.parameters.reload || 0) + 1,
            })
        })
    }

    public render() {
        const { bottleneckBeta } = this.props
        const { observation, fingerprint } = this.props.errorGroup
        const isBottleneckObservation = fingerprint.startsWith('bottleneck-')
        let bottleneckType: string | null = null

        let subHeader
        let description = (
            <p>
                {observation.description} (
                <a
                    href={observation.link + '?utm_medium=app&utm_source=observation&utm_campaign=observation-help'}
                    target="_blank"
                    rel="noreferrer"
                >
                    More information
                </a>
                )
            </p>
        )

        if (isBottleneckObservation) {
            bottleneckType = fingerprint.replace('bottleneck-', '')

            description = <BottleneckView type={bottleneckType} />
            if (observation.affectedOrigins.All) {
                const origin = observation.affectedOrigins.All
                subHeader = (
                    <span>
                        <span className="text-muted">Count</span> <LargeNumber class="" value={parseInt(origin.value, 10)} />{' '}
                        <span className="text-muted">Last Recorded</span>{' '}
                        <strong>
                            <MomentDate date={moment.unix(origin.timestamp).utc()} />
                        </strong>
                    </span>
                )
            }
        }

        const activeOption = <i className="fa fa-check-circle-o tw-mr-8" />
        const inactiveOption = <i className="fa fa-circle-o tw-mr-8" />

        return (
            <React.Fragment>
                <Box>
                    <BoxHeader>
                        <BoxTitle>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <ObservationIcon observation={this.props.errorGroup} />
                                <div>
                                    {observation.state === ObservationState.OK ? observation.labelOff : observation.label}
                                    <div className="tw-font-size-14">{subHeader ? subHeader : null}</div>
                                </div>
                                {bottleneckBeta ? (
                                    <div style={{ marginLeft: 'auto' }}>
                                        <a
                                            href="#"
                                            className="btn btn-sm btn-default dropdown-toggle"
                                            data-toggle="dropdown"
                                            aria-expanded="true"
                                        >
                                            State: <span className="tw-font-weight-strong">{this.ignoreStateLabel(observation.state)}</span>
                                        </a>
                                        <ul className="dropdown-menu dropdown-menu-right">
                                            <li>
                                                <a
                                                    onClick={() =>
                                                        this.updateIgnoreState('XhprofObservationConfiguration.unignore', fingerprint)
                                                    }
                                                >
                                                    {observation.state !== ObservationState.IGNORE ? activeOption : inactiveOption}
                                                    {this.ignoreStateLabel(ObservationState.OK)}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    onClick={() =>
                                                        this.updateIgnoreState('XhprofObservationConfiguration.ignore', fingerprint)
                                                    }
                                                >
                                                    {observation.state === ObservationState.IGNORE ? activeOption : inactiveOption}
                                                    {this.ignoreStateLabel(ObservationState.IGNORE)}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        </BoxTitle>
                    </BoxHeader>
                    <BoxBody>
                        <div className="tw-font-size-18">{description}</div>
                    </BoxBody>
                </Box>

                {isBottleneckObservation ? null : (
                    <>
                        <ul className="nav nav-pills nav-pills-tideways">
                            <li className="active">
                                <a href="#servers" data-target="#servers" data-toggle="tab">
                                    Affected Servers
                                </a>
                            </li>
                        </ul>

                        <Box>
                            <BoxBody noPadding className="tw-min-height-48">
                                <div className="tab-content no-padding">
                                    <div className="tab-pane active" id="servers">
                                        {this.renderStateDetails(observation)}
                                    </div>
                                </div>
                            </BoxBody>
                        </Box>
                    </>
                )}

                {isBottleneckObservation && observation.traces ? (
                    <Box>
                        <BoxHeader flexbox>
                            <BoxTitle>Recent Traces collected</BoxTitle>
                            <div>
                                <Link
                                    className="btn btn-sm btn-default tw-mr-8"
                                    route="XhprofProfilerBundle.Profile.list"
                                    params={{ bottleneckType }}
                                >
                                    Show All
                                </Link>
                            </div>
                        </BoxHeader>
                        <BoxBody noPadding>
                            <ProfileTable
                                compact
                                profiles={observation.traces}
                                profileRoute="XhprofProfilerBundle.Profile.bottlenecksReport"
                            />
                        </BoxBody>
                    </Box>
                ) : null}
            </React.Fragment>
        )
    }

    private ignoreStateLabel = (state: ObservationState): string => {
        switch (state) {
            case ObservationState.IGNORE:
                return 'Ignored'
            default:
                return 'Active'
        }
    }

    private renderStateDetails(observation) {
        if (observation.state === ObservationState.OK) {
            return <p className="alert alert-success tw-mt-16">This observation is not a problem in the current service.</p>
        }

        if (observation.state === ObservationState.UNKNOWN) {
            return (
                <p className="alert alert-info tw-mt-16">
                    No information about this observation was reported from servers in this service.
                </p>
            )
        }

        return (
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th>Affected Server</th>
                        <th>{observation.valueLabel || 'Value'}</th>
                        <th>Time</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(observation.affectedOrigins).map(([name, origin]: [string, ErrorGroupObservationAffectedOrigin]) => (
                        <tr key={name}>
                            <td width={'50%'}>{extractServerDisplayNameFromHostname(name)}</td>
                            <td width={'25%'}>{origin.value || '-/-'}</td>
                            <td width={'25%'}>
                                <MomentDate date={moment.unix(origin.timestamp).utc()} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )
    }
}
